import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import TableOrganism from "../../organisms/Table/TableOrganism";
import { createHeaderRow } from "../../utilities/CreateHeaderRow";
import { createReturnsRows } from "./CreateReturnsTable";
import { TableRowProps } from "../../molecules/TableRow/TableRowAtom";
import { CallToActionButton } from "../../atoms/callToActionButton/CallToActionButton";
import { useLoaderDispatch } from "../../context/LoaderContext";
import { startLoading, stopLoading } from "../../context/actions/loaderActions";
import { largeButton } from "../../utilities/style";
import useApiService from "../../utilities/useApiService";
import AreYouSure from "../../molecules/AreYouSure/AreYouSure";
import { Button, Input } from "@material-ui/core";
import { Box, Pagination } from "@mui/material";
import { Container, ButtonWrapper, useStyles } from "./ReturnPageStyles";
import {
  ReturnRequestServiceType,
  ReturnRequestStatus,
  UserRole,
} from "../../utilities/Enums";
import {
  isAdmin,
  isWholesalerUser,
  isRoleIncluded,
  isFieldProcessorUser,
} from "../../utilities/checkUserRole";
import { useAuthState } from "../../context/AuthContext";
import { usePharmacyEnabledStatus } from "../../context/PharmacyEnabledStatusContext";
import {
  DEFAULT_RETURN_REQUESTS_LIST_QUERY,
  buildReturnRequestsListSearch,
  parseReturnRequestsListQuery,
} from "../../utilities/returnRequestsListQuery";
import DeaAndStateLicenseStatus from "../../molecules/DeaAndStateLicenseStatus/DeaAndStateLicenseStatus";
import { checkExpiry } from "../../molecules/DeaAndStateLicenseStatus/expiryUtils";
import Loader from "../../atoms/Loader/LoaderAtom";
import EditableModal from "../../atoms/Editable Modal/EditableModal";
import PrintHeaderInfo from "../../organisms/PrintHeaderInfo/PrintHeaderInfo";
import ModalWithText from "../../molecules/ModalWithText/ModalWithText";
import AppBreadcrumbs from "../../organisms/Breadcrumbs/Breadcrumbs";
import { toastFail } from "../../utilities/toast";

export interface ReturnPageProps {
  pharmacy: number;
}
interface PharmacyInfo {
  pharmacy: {
    deaExp: string;
    licenseStateExp: string;
  };
}
export interface NoteContext {
  note: Note | null;
  returnRequestId: number;
  pharmacyId: number;
  userRole: string;
}

interface Note {
  id: number;
  text: string;
  seen: boolean;
}

export const ReturnPage: React.FC<ReturnPageProps> = (
  props: ReturnPageProps
) => {
  const { user } = useAuthState();
  const userRole = user!.role;
  const history = useHistory();
  const showAllReturns =
    history.location.pathname.indexOf("/admin/all/pharmacies/returns") !== -1;
  const { deleteMethod, getMethod, putMethod } = useApiService();
  const params: any = useParams();
  const loaderDispatch = useLoaderDispatch();
  const queryClient = useQueryClient();
  const classes = useStyles();


  const [responseData, setResponseData] = useState<any>();

  const pageSize = 15;
  const { pharmacyEnabledStatusMap, setPharmacyEnabledStatus } =
    usePharmacyEnabledStatus();

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [toBeDeletedObject, setToBeDeletedObject] = useState<{ id: number; pharmacyId: number } | null>(null);

  const [expiryType, setExpiryType] = useState<"DEA" | "State">("DEA");
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const [pharmacyInfo, setPharmacyInfo] = useState<PharmacyInfo | null>(null);

  const [openNoteModal, setOpenNoteModal] = useState(false);
  const [noteContext, setNoteContext] = useState<NoteContext | null>(null);

  const location = useLocation();
  const showSearchAndFilters =
    location.pathname === `/admin/all/pharmacies/returns`;
  const isAdminRoute = location.pathname.startsWith("/admin");
  const basePath = isAdminRoute ? "/admin/pharmacies" : "/pharmacies";
  const listQuery = useMemo(
    () => parseReturnRequestsListQuery(location.search),
    [location.search]
  );
  const {
    page: apiPage,
    searchValue,
    status: selectedStatus,
    serviceType: selectedServiceType,
    startDate,
    endDate,
  } = listQuery;
  const currentPage = apiPage + 1;

  const updateListQuery = (
    updates: Partial<typeof DEFAULT_RETURN_REQUESTS_LIST_QUERY>,
    push = false
  ) => {
    const nextSearch = buildReturnRequestsListSearch({
      ...listQuery,
      ...updates,
    });
    if (nextSearch === location.search) {
      return;
    }
    const nextLocation = {
      pathname: location.pathname,
      search: nextSearch,
    };
    if (push) {
      history.push(nextLocation);
    } else {
      history.replace(nextLocation);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "startDate" | "endDate"
  ) => {
    updateListQuery({ [field]: e.target.value, page: 0 });
  };

  const handleClear = () => {
    updateListQuery(DEFAULT_RETURN_REQUESTS_LIST_QUERY);
  };

  const getReturnRequestsApi = async (page: number, size: number) => {
    startLoading(loaderDispatch);
    let response;
    if (userRole === UserRole.WholesalerUser) {
       response = await getMethod(
          `/pharmacies/${params.pharmacyId}/returnrequests-wholesaleruser`
       );
    } else {
       if (showAllReturns) {
          response = await getMethod(
             `/pharmacies/returnrequests?page=${page}&size=${size}&searchValue=${searchValue}&status=${selectedStatus}&serviceType=${selectedServiceType}&startDate=${startDate}&endDate=${endDate}`
          );
       } else {
          response = await getMethod(
             `/pharmacies/${params.pharmacyId}/returnrequests?page=${page}&size=${size}&searchValue=${searchValue}&status=${selectedStatus}&serviceType=${selectedServiceType}&startDate=${startDate}&endDate=${endDate}`
          );
       }
    }
    stopLoading(loaderDispatch);
    if(response.data){
      response.data.content.forEach(({ returnRequest: { pharmacy } }: any) => {
        if (pharmacy && pharmacy?.id) {
          setPharmacyEnabledStatus(pharmacy.id, pharmacy.enabled);
        }
      });
      setResponseData(response.data);
    }
    return response.data.content;
  };
  const { data: returnRequests, isFetching, refetch } = useQuery(
    [
      "return-requests",
      currentPage,
      pageSize,
      searchValue,
      selectedStatus,
      selectedServiceType,
      startDate,
      endDate,
    ],
    () => getReturnRequestsApi(apiPage, pageSize),
    {
      keepPreviousData: true,
    }
  );
  
  const getStatusOptionsApi = async () => {
    if (!isWholesalerUser(userRole)) {
      const response = await getMethod(
        `/pharmacies/returnrequests/return-request-status`
      );
      return response.data;
    }
  };
  const { data: statusOptions } = useQuery(
    "status-options",
    getStatusOptionsApi
  );

  const deleteReturnRequestApi = async ({ id, pharmacyId }: any) => {
    startLoading(loaderDispatch);
    if (isAdmin(userRole)) {
        await deleteMethod(`/pharmacies/${pharmacyId}/returnrequests/${id}`);
    }
    stopLoading(loaderDispatch);
    return id;
  };
  const { mutate: deleteReturnRequest } = useMutation(deleteReturnRequestApi, {
    onSuccess: (id) => {
      const newData = [...returnRequests];
      newData.forEach((item, i) => {
        if (item.returnRequest.id === id) {
          newData.splice(i, 1);
          return;
        }
      });
      queryClient.invalidateQueries(["return-requests", currentPage]);
    },
  });

  const updateStatusApi = async ({
    selectedOption,
    pharmacyId,
    requestId,
  }: any) => {
    let response;
    response = await putMethod(
      `/pharmacies/${pharmacyId}/returnrequests/${requestId}/status`,
      {
        returnRequestStatus: selectedOption,
      }
    );
    return { updatedRetReq: response.data, requestId };
  };
  const { mutate: updateStatus } = useMutation(updateStatusApi, {
    onSuccess: () => {
      queryClient.invalidateQueries("return-requests");
    },
  });

  const getPharmacyInfoApi = async () => {
    const response = await getMethod(`/pharmacies/${params.pharmacyId}/full`);
    return response.data;
  };

  useEffect(() => {

    setPharmacyInfo(null);
    
    const fetchPharmacyInfo = async () => {
      if (!params.pharmacyId || showAllReturns) return;
      if (!returnRequests || returnRequests.length > 0) return;
      try {
        const data = await getPharmacyInfoApi();
        setPharmacyInfo(data);
      } catch {
        toastFail("Unable to verify pharmacy license. Please try again.");
      }
    };

    fetchPharmacyInfo();
  }, [returnRequests, params.pharmacyId, showAllReturns]);

  const allExpirations = () => {
    if (returnRequests?.length) {
      return returnRequests.map((item: any) => ({
        deaExp: item.returnRequest.pharmacy.deaExp,
        licenseStateExp: item.returnRequest.pharmacy.licenseStateExp,
      }));
    }
    if (pharmacyInfo) {
      return [
        {
          deaExp: pharmacyInfo.pharmacy.deaExp,
          licenseStateExp: pharmacyInfo.pharmacy.licenseStateExp,
        },
      ];
    }
    return [];
  };

  const expiryDataNotReady =
  !showAllReturns &&
  (!returnRequests || (returnRequests.length === 0 && !pharmacyInfo));

  const handleClick = async () => {

    if (expiryDataNotReady) return;

    const isUserRole = isRoleIncluded([UserRole.User, UserRole.FieldProcessor], userRole);
  
    for (const expiration of allExpirations()) {
      if (
        checkExpiry(
          expiration.deaExp,
          "DEA",
          isUserRole,
          setExpiryType,
          setIsPopupOpen
        ) ||
        checkExpiry(
          expiration.licenseStateExp,
          "State",
          isUserRole,
          setExpiryType,
          setIsPopupOpen
        )
      ) {
        return;
      }
    }
    if (isRoleIncluded([UserRole.User], userRole)) {
      history.push(`/pharmacies/${params.pharmacyId}/returnrequest/create`);
    }  else {
      history.push(`/admin/pharmacies/${params.pharmacyId}/returnrequests/add`);
    }
  };

  const getHeaderTitles = () => {
    let headerTitles = ["Number", "Created At"];
    if (isRoleIncluded([UserRole.Admin, UserRole.Warehouse, UserRole.FieldProcessor], userRole) && showAllReturns) {
      headerTitles.push("Pharmacy", "User");
    }
    if (!isWholesalerUser(userRole)) {
      headerTitles.push("Items");
    }
    headerTitles.push("Status");
    if (!isWholesalerUser(userRole)) {
      headerTitles.push("UPS Shipments");
    }
    headerTitles.push("Reports");
    headerTitles.push("Service Type", "Preferred Date");
    if(!isFieldProcessorUser(userRole)){
      headerTitles.push("Notes");
    }
    if (
      (showAllReturns || pharmacyEnabledStatusMap[params.pharmacyId]) &&
      isRoleIncluded([UserRole.Admin, UserRole.Warehouse], userRole)
    ) {
      headerTitles.push("", "");
    }
    if (isFieldProcessorUser(userRole)) {
      headerTitles.push("Note");
    }
    headerTitles.push("");
    return headerTitles;
  };

  const renderCreateLabelButton = (item: any) => {
    const pharmacyId = params.pharmacyId
      ? params.pharmacyId
      : item.returnRequest.pharmacy?.id;

    const handleClick = () => {
      history.location.pathname.indexOf("admin") !== -1
        ? history.push(
            `/admin/pharmacies/${item.returnRequest.pharmacy.id}/returnrequests/${item.returnRequest.id}/shipments/create`
          )
        : history.push(
            `/pharmacies/${item.returnRequest.pharmacy.id}/returnrequests/${item.returnRequest.id}/shipments/create`
          );
    };

    return (
      <ButtonWrapper>
        <Button
          onClick={handleClick}
          disableElevation
          className="createLabelButton"
          disabled={!pharmacyEnabledStatusMap[pharmacyId] || isRoleIncluded([UserRole.Warehouse], userRole)}
        >
          Create Shipment
        </Button>
      </ButtonWrapper>
    );
  };

  const Filters = ({
    selectedStatus,
    selectedServiceType,
  }: {
    selectedStatus: string;
    selectedServiceType: string;
  }) => {
    return (
      <div className={classes.filter}>
        <div>
          <label>Status:</label>
          <select
            value={selectedStatus ?? ""}
            onChange={(e) =>
              updateListQuery({ status: e.target.value, page: 0 })
            }
          >
            <option value="">All</option>
            {ReturnRequestStatus &&
              Object.values(ReturnRequestStatus).map((status, index) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
          </select>
        </div>
        <div>
          <label>Service Type:</label>
          <select
            value={selectedServiceType ?? ""}
            onChange={(e) =>
              updateListQuery({ serviceType: e.target.value, page: 0 })
            }
          >
            <option value="">All</option>
            {ReturnRequestServiceType &&
              Object.values(ReturnRequestServiceType).map((service, index) => (
                <option key={service} value={service}>
                  {service}
                </option>
              ))}
          </select>
        </div>
        <div>
          <label>Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => handleChange(e, "startDate")}
          />
        </div>
        <div>
          <label>End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => handleChange(e, "endDate")}
          />
        </div>
        <button className="ClearButton" onClick={handleClear}>
          Clear
        </button>
      </div>
    );
  };

  const markNoteAsSeen = useCallback(
    async (id: number) => {
      await putMethod(`/pharmacies/note/${id}`);
      await refetch();
    },
    [putMethod, refetch]
  );

  const handleClickNote = async (entry: any, userRole: string) => {
    const note = entry.returnRequest.note || null;
    setOpenNoteModal(true);
    if (note && !note.seen && !isFieldProcessorUser(userRole)) {
      setTimeout(() => {
        markNoteAsSeen(note.id);
      }, 300);
    }

    setNoteContext({
      note,
      returnRequestId: entry.returnRequest.id,
      pharmacyId: entry.returnRequest.pharmacy.id,
      userRole,
    });
  };

  const rows: Array<TableRowProps> = useMemo(() => {
    return returnRequests
      ? createReturnsRows(
          returnRequests,
          history,
          userRole,
          showAllReturns,
          setOpenConfirmDelete,
          setToBeDeletedObject,
          updateStatus,
          statusOptions ?? [],
          renderCreateLabelButton,
          getMethod,
          handleClickNote
        )
      : [];
  }, [returnRequests, statusOptions]);
  

  const renderCreateReturnRequestButton = () => {
    if (shouldCreateButtonBeVisible()) {
      return (
        <div>
        <DeaAndStateLicenseStatus type={expiryType} open={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <CallToActionButton
            height={"80px"}
            text="Start New Return"
            width={largeButton.width}
            onClick={handleClick}
            disabled={!pharmacyEnabledStatusMap[params.pharmacyId] || expiryDataNotReady}
          />
          </div>
        </div>
      );
    }
  };

  const shouldCreateButtonBeVisible = () => {
    return (
      isRoleIncluded(
        [
          UserRole.Admin,
          UserRole.User,
          UserRole.FieldProcessor,
          UserRole.Warehouse,
        ],
        userRole
      ) && !showAllReturns
    );
  };

  const shouldPharmacyNameBeVisible = () => {
    return !showAllReturns;
  };

  const renderPharmacyInfo = () => {
    if (shouldPharmacyNameBeVisible() && returnRequests && !isFetching) {
      return (
        <PrintHeaderInfo
          pharmacyName={
            returnRequests[0].returnRequest.pharmacy.doingBusinessAs
          }
        />
      );
    }
  };

  const renderAreYouSureDialog = () => {
    if (openConfirmDelete && toBeDeletedObject) {
      return (
        <AreYouSure
          open={openConfirmDelete}
          onClose={() => {
            setOpenConfirmDelete(false);
            setToBeDeletedObject(null);
          }}
          toBeDeletedObject={toBeDeletedObject}
          deleteObject={() => {
            if (toBeDeletedObject) {
              deleteReturnRequest({
                id: toBeDeletedObject.id,
                pharmacyId: toBeDeletedObject.pharmacyId,
              });
            }
          }}
        />
      );
    }
  };

  const handleSaveNote = async (editedNote: string) => {
    if (!noteContext) return null;
    await putMethod(
      `/pharmacies/${noteContext.pharmacyId}/returnrequests/${noteContext.returnRequestId}/note`,
      {
        text: editedNote,
      }
    );
    handleCloseModal();
    await refetch();
  };

  const handleCloseModal = () => {
    setOpenNoteModal(false);
    setNoteContext(null);
  };

  const renderNoteModal = () => {
    if (!noteContext) return null;
    const noteText = noteContext.note?.text || "";
    if (isFieldProcessorUser(noteContext.userRole)) {
      return (
        <EditableModal
          open={openNoteModal}
          selectedNote={noteText}
          saveNote={handleSaveNote}
          onCloseModal={handleCloseModal}
        />
      );
    } else {
      return (
        <ModalWithText
          open={openNoteModal}
          content={noteText}
          onClose={handleCloseModal}
        />
      );
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    updateListQuery({ page: page - 1 }, true);
  };


  const pharmacyName =
    returnRequests?.[0]?.returnRequest?.pharmacy?.doingBusinessAs || "";

  const breadcrumbItems =
    !showAllReturns && pharmacyName
      ? [
          {
            label: pharmacyName,
            path: `${basePath}/${params.pharmacyId}`,
          },
          {
            label: "Returns",
          },
        ]
      : [];

  return (
    <Container>
      {renderPharmacyInfo()}
      <div className={classes.breadcrumbsContainer}>
        <AppBreadcrumbs items={breadcrumbItems} />
      </div>
      {showSearchAndFilters && (
        <Box>
          <Input
            id="searchField"
            type="text"
            value={searchValue}
            onChange={(e) => {
              updateListQuery({ searchValue: e.target.value, page: 0 });
            }}
            placeholder="Search By User Account or By Pharmacy Name"
            className={classes.searchField}
          />
          <Filters
            selectedStatus={selectedStatus}
            selectedServiceType={selectedServiceType}
          />
        </Box>
      )}
      {renderCreateReturnRequestButton()}
      {!isFetching ? (
        <TableOrganism
          header={createHeaderRow(getHeaderTitles())}
          rows={rows}
        />
      ) : (
        <Loader />
      )}
      {renderAreYouSureDialog()}
      {renderNoteModal()}
      <Box className={classes.paginationContainer}>
        <Pagination
          count={responseData?.totalPages}
          page={currentPage}
          onChange={handlePageChange}
        />
      </Box>
    </Container>
  );
};
